import { useEffect, useState } from "react";
import { useApolloClient, DocumentNode, useLazyQuery } from "@apollo/client";

/**
 * Hook to ensure workplace data is fetched and available in cache.
 *
 * This hook handles the case where GET_ROOT_WORKPLACES only fetches minimal fields,
 * but edit pages need full workspace data. It will:
 * 1. Try to read from cache first
 * 2. If cache data is incomplete/missing, fetch using individual workspace query (GET_ROOM, GET_FLOOR, etc.)
 *
 * The individual query will populate the cache with full data for that specific workspace.
 *
 * @param id - Workspace ID
 * @param typename - GraphQL typename (e.g., "Room", "Floor", etc.)
 * @param fragment - GraphQL fragment to read from cache
 * @param fragmentName - Name of the fragment
 * @param query - GraphQL query to fetch full data (GET_ROOM, GET_FLOOR, etc.)
 * @param onDataUpdate - Callback when data is available
 * @returns {loading, error} - Loading state and error if any
 */
export const useWorkplaceData = <T>({
  id,
  typename,
  fragment,
  fragmentName,
  query,
  onDataUpdate,
}: {
  id: string;
  typename: string;
  fragment: DocumentNode;
  fragmentName: string;
  query: DocumentNode;
  onDataUpdate: (data: T | null) => void;
}) => {
  const client = useApolloClient();
  const [isLoading, setIsLoading] = useState(true);
  const [fetchWorkplace, { loading: queryLoading, error }] = useLazyQuery(
    query,
    {
      variables: { id },
      fetchPolicy: "network-only",
    }
  );

  useEffect(() => {
    const loadData = async () => {
      // Try to read from cache first
      let data = client.readFragment<T>({
        id: `${typename}:${id}`,
        fragmentName,
        fragment,
      });

      if (!data) {
        // Cache doesn't have the data, fetch it
        try {
          setIsLoading(true);
          await fetchWorkplace();

          // Try reading from cache again after fetch
          data = client.readFragment<T>({
            id: `${typename}:${id}`,
            fragmentName,
            fragment,
          });
        } catch (error) {
          console.error(`Error fetching ${typename}:`, error);
        }
      }

      onDataUpdate(data);
      setIsLoading(false);
    };

    loadData();
  }, [id]);

  return { loading: isLoading || queryLoading, error };
};
